import { Menu, MenuItem } from "@mui/material";
import React, { useState } from "react";

import DefaultStatisticsCard from "@/components/templates/Cards/StatisticsCards/DefaultStatisticsCard";
import { formatCurrency } from "@/helpers/currency-helpers";
import { getDateRange } from "@/helpers/date-helpers";
import useGetTotalOrderAmount from "@/hooks/requests/dashboard/sales/use-get-total-order-amount";
import { SalesDashboardMessages } from "@/messages/salesDashboard/sales-dashboard.messages";
import { DateRange } from "@/types/date.types";

import { getDateRangeMessages, getDateRangeOptions } from "../constants";

type TotalOrderAmountCardProps = {
  messages: SalesDashboardMessages;
};

export default function TotalOrderAmountCard({ messages }: TotalOrderAmountCardProps) {
  const [dateRangeOption, setDateRangeOption] = useState<DateRange>(DateRange.TODAY);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { startDate, endDate } = getDateRange(dateRangeOption);
  const { data, isLoading } = useGetTotalOrderAmount({ startDate, endDate });

  const dateRangeOptions = getDateRangeOptions(messages);
  const dateRangeMessages = getDateRangeMessages(messages);
  const isSmallThan0 = data?.percentageIncrease && data?.percentageIncrease < 0;
  const percentageColor = isSmallThan0 ? "error" : "success";

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const renderMenu = (
    <Menu
      anchorEl={menuAnchorEl}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      open={Boolean(menuAnchorEl)}
      onClose={handleMenuClose}
      keepMounted
      disableAutoFocusItem
    >
      {dateRangeOptions.map((option) => (
        <MenuItem
          key={option.value}
          onClick={() => {
            setDateRangeOption(option.value);
            handleMenuClose();
          }}
        >
          {option.label}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <DefaultStatisticsCard
      title={messages.salesTitle}
      isLoading={isLoading}
      count={formatCurrency(data?.currentPeriodTotalOrderAmount || 0)}
      percentage={{
        color: percentageColor,
        value: `${data?.percentageIncrease}%`,
        label: dateRangeMessages[dateRangeOption] as string,
      }}
      dropdown={{
        action: handleMenuOpen,
        menu: renderMenu,
        value: dateRangeOptions.find((option) => option.value === dateRangeOption)?.label as string,
      }}
    />
  );
}
